import { useEffect, useState, useMemo } from "react";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { DEFINED_PERMISSIONS } from "../../../../../utils/const";
import { FormatDateTime } from "../../../../../utils/functions/ResourceFunctions";
import { useSearchFilter } from "../../../../../utils/functions/useDate";
import {
  useDeleteCommitteMutation,
  useGetCommitteQuery,
} from "../../../../api/hrApi";
import { CTAButtons } from "../../../../global/components/Buttons/buttons";
import PageStyle from "../../../Components/Layout/PageLayout";
import { TableActions } from "../../../Components/Misc/Actions";
import DeleteItemModal from "../../../Components/Modals/DeleteItemModal";
import { AppModalTemplate } from "../../../Components/Modals/Modals";
import Pagination from "../../../Components/Pagination/Pagination";
import {
  CalendarFilter2,
  ProDropFilter,
  SearchFilter,
} from "../../../Components/Search/Search";
import Table from "../../../Components/Table/Table";
import { getStatusText } from "../../Finance/CostCenter/Class";
import { FilterByStatus } from "./status";

import DashboardStyle from "../../../../dashboard/Styles/Dashboard.module.css";
import CreateCommitteeIndexModal from "./Modals/CommitteModals";
import { CommitteKRAModal } from "./Modals/CommitteModals/CommitteeKRAModal";
import CommitteeDetailModal from "./Modals/CommitteModals/DetailModal";
import MemberModal from "./Modals/CommitteModals/MemberModal";
import EditCommitteeIndexModal from "./Modals/CommitteModals/EditCommitteeIndexModal";
import EditMemberModal from "./Modals/CommitteModals/EditMemberModal";
import EditDetailModal from "./Modals/CommitteModals/EditDetailModal";
import EditCommitteeKRAModal from "./Modals/CommitteModals/EditCommitteeKRAModal";

const CommitteeList = () => {
  const [onDelete] = useDeleteCommitteMutation();
  const [openModal, setOpenModal] = useState(false);
  const [openCreatModal, setOpenCreatModal] = useState(false);
  const [openMemberModal, setOpenMemberModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [initialModal, setInitialModal] = useState({
    committeeHeadId: 0,
    departmentId: 0,
    description: "",
    title: "",
  });
  const [committeeFloss, setCommitteeFloss] = useState({
    selectedMembers: [],
    memberIds: [0],
  });
  const [detailModal, setDetailModal] = useState(false);
  const [kraModal, setKraModal] = useState(false);
  const [matchedPermissionHr, setMatchedPermissionHr] = useState("");
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");

  // Step tracking for flows
  const [currentCreateStep, setCurrentCreateStep] = useState(1); // 1: Committee, 2: Member, 3: Detail, 4: KRA
  const [currentEditStep, setCurrentEditStep] = useState(1); // 1: Committee, 2: Member, 3: Detail, 4: KRA

  // Edit flow state variables
  const [openEditMemberModal, setOpenEditMemberModal] = useState(false);
  const [openEditDetailModal, setOpenEditDetailModal] = useState(false);
  const [openEditKraModal, setOpenEditKraModal] = useState(false);
  const [editInitialModal, setEditInitialModal] = useState({
    committeeHeadId: 0,
    departmentId: 0,
    description: "",
    title: "",
  });
  const [editCommitteeFloss, setEditCommitteeFloss] = useState({
    selectedMembers: [],
    memberIds: [0],
  });

  // Form data cache for CREATE flow
  const [cachedCreateData, setCachedCreateData] = useState({
    committee: null,
    members: null,
    detail: null,
    kra: null,
  });

  // Form data cache for EDIT flow
  const [cachedEditData, setCachedEditData] = useState({
    committee: null,
    members: null,
    detail: null,
    kra: null,
  });

  useEffect(() => {
    const userPerm = JSON.parse(localStorage.getItem("x-u-perm"));
    const hrPerm = [
      DEFINED_PERMISSIONS.SeniorHRManager,
      DEFINED_PERMISSIONS.PrimaryHRManager,
    ];

    // Check if userPerm exists and matches any permission in hrPerm
    if (userPerm && userPerm.some((perm) => hrPerm.includes(perm))) {
      const matchedPerm = userPerm.find((perm) => hrPerm.includes(perm));
      setMatchedPermissionHr(matchedPerm); // Set matchedPermissionHr to the matched permission
    } else {
      setMatchedPermissionHr(""); // Reset to empty string if not matched
    }
  }, []);
  const navigate = useNavigate();
  const handleModal = () => {
    setOpenModal(!openModal);
  };
  const handleMemberModal = () => {
    setOpenMemberModal(!openMemberModal);
  };
  const handleDetailModal = () => {
    setDetailModal(!detailModal);
  };
  // Function to open the initial create modal
  const handleOpenCreateModale = () => {
    setOpenCreatModal(true);
  };

  // Function to close create modal
  const handleCloseCreateModal = () => {
    setOpenCreatModal(false);
    setCurrentCreateStep(1); // Reset to first step
    // Clear cached create data
    setCachedCreateData({
      committee: null,
      members: null,
      detail: null,
      kra: null,
    });
  };
  const handleKraModal = () => {
    setKraModal(!kraModal);
  };
  // Function called when committee modal validation passes
  const handleCommitteeValidation = (data) => {
    console.log("Committee data validated:", data);
    // Cache the committee data
    setCachedCreateData((prev) => ({ ...prev, committee: data }));
    setCurrentCreateStep(2);
    setOpenCreatModal(false); // Close committee modal
    setOpenMemberModal(true); // Open member modal
  };

  // Edit flow handlers
  const handleEditMemberModal = () => {
    setOpenEditMemberModal(!openEditMemberModal);
  };

  const handleEditDetailModal = () => {
    setOpenEditDetailModal(!openEditDetailModal);
  };

  const handleEditKraModal = () => {
    setOpenEditKraModal(!openEditKraModal);
  };

  // Function called when edit committee modal validation passes
  const handleEditCommitteeValidation = (data) => {
    console.log("Edit committee data validated:", data);
    // Cache the edit committee data
    setCachedEditData((prev) => ({ ...prev, committee: data }));
    setCurrentEditStep(2);
    setOpenModal(false); // Close edit committee modal
    setOpenEditMemberModal(true); // Open edit member modal
  };

  // Back navigation handlers for CREATE flow
  const handleCreateBackFromMember = () => {
    setOpenMemberModal(false);
    setOpenCreatModal(true);
    setCurrentCreateStep(1);
  };

  const handleCreateBackFromDetail = () => {
    setDetailModal(false);
    setOpenMemberModal(true);
    setCurrentCreateStep(2);
  };

  const handleCreateBackFromKRA = () => {
    setKraModal(false);
    setDetailModal(true);
    setCurrentCreateStep(3);
  };

  // Back navigation handlers for EDIT flow
  const handleEditBackFromMember = () => {
    setOpenEditMemberModal(false);
    setOpenModal(true);
    setCurrentEditStep(1);
  };

  const handleEditBackFromDetail = () => {
    setOpenEditDetailModal(false);
    setOpenEditMemberModal(true);
    setCurrentEditStep(2);
  };

  const handleEditBackFromKRA = () => {
    setOpenEditKraModal(false);
    setOpenEditDetailModal(true);
    setCurrentEditStep(3);
  };
  const {
    searchText,
    setSearchText,
    pageSize,
    setPageSize,
    setCurrentPage,
    currentPage,
    filter,
    setFilter,
  } = useSearchFilter({
    initialFilter: 1,
  });
  const { data, isLoading } = useGetCommitteQuery({
    searchTerm: searchText,
    pageNumber: currentPage,
    pageSize: pageSize,
    createdDateFrom: startDate,
    createdDateTo: endDate,
    status: filter,
    includeInactive: false,
  });
  const actionButton = (
    <>
      <CTAButtons onClick={handleOpenCreateModale}>
        Create Committee List
      </CTAButtons>
    </>
  );
  const handleOpenDeleteModal = (id) => {
    if (id) {
      setSelectedId(id);
      setOpenDelete(true);
    }
  };
  const handleCloseDelete = () => {
    setSelectedId(null);
    setOpenDelete(false);
  };
  const handleDelete = async () => {
    try {
      const response = await onDelete({
        id: parseInt(selectedId),
      }).unwrap();
      if (response?.successful) {
        handleCloseDelete();
        toast.success(response?.statusMessage);
      } else {
        toast.error(response?.data?.title);
      }
    } catch (error) {
      toast.error(error.message ?? "Error deleting rating scale");
    }
  };
  const handleView = (id) => {
    if (id) {
      navigate(`${id}/view`);
    }
  };
  const handleEdit = (id) => {
    if (id) {
      setSelectedId(id);
      setCurrentEditStep(1); // Reset edit flow to first step
      handleModal();
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortedData = useMemo(() => {
    if (!data?.responseObject?.pageItems) return [];
    return [...data.responseObject.pageItems].sort((a, b) => {
      if (!sortColumn) return 0;
      let aValue = a[sortColumn];
      let bValue = b[sortColumn];
      if (sortColumn === "createdDate") {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      } else if (sortColumn === "members") {
        aValue = aValue?.length || 0;
        bValue = bValue?.length || 0;
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [data?.responseObject?.pageItems, sortColumn, sortDirection]);

  const renderSortIcon = (column) => {
    if (sortColumn !== column) return <FaSort className="ml-1" />;
    return sortDirection === "asc" ? (
      <FaSortUp className="ml-1" />
    ) : (
      <FaSortDown className="ml-1" />
    );
  };

  return (
    <PageStyle
      action={actionButton}
      hasBack={true}
      backURL={"/dashboard/hr/HR-Appraisal-Setup"}
      hasTopPageDemo
      support={"Used for managing committee."}
      demoTitle={"Committee List View"}
    >
      <form className={DashboardStyle.dashboard_filter}>
        <SearchFilter
          value={searchText}
          text={(searchText) => setSearchText(searchText)}
        />
        <CalendarFilter2
          name="Select Date Range"
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          normalDate
        />
        <ProDropFilter
          filter={filter}
          setFilter={setFilter}
          name={"Status"}
          filterBy={FilterByStatus}
        />
      </form>
      <div className={DashboardStyle.dashboard_table_holder}>
        <Table>
          <thead>
            <tr>
              <th>S/N</th>
              <th
                onClick={() => handleSort("title")}
                style={{ cursor: "pointer" }}
              >
                Title {renderSortIcon("title")}
              </th>
              <th
                onClick={() => handleSort("committeeHeadName")}
                style={{ cursor: "pointer" }}
              >
                Committee Head Name {renderSortIcon("committeeHeadName")}
              </th>
              <th
                onClick={() => handleSort("departmentName")}
                style={{ cursor: "pointer" }}
              >
                Department Name {renderSortIcon("departmentName")}
              </th>
              <th
                onClick={() => handleSort("members")}
                style={{ cursor: "pointer" }}
              >
                No of Committee Members {renderSortIcon("members")}
              </th>
              <th
                onClick={() => handleSort("initiatorName")}
                style={{ cursor: "pointer" }}
              >
                Initiator's Name {renderSortIcon("initiatorName")}
              </th>
              <th
                onClick={() => handleSort("createdDate")}
                style={{ cursor: "pointer" }}
              >
                Created Date {renderSortIcon("createdDate")}
              </th>
              <th
                onClick={() => handleSort("approvalStatus")}
                style={{ cursor: "pointer" }}
              >
                Status {renderSortIcon("approvalStatus")}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={6} className="text-center">
                  Loading...
                </td>
              </tr>
            )}
            {sortedData?.map((item, index) => {
              return (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{item?.title}</td>
                  <td>{item?.committeeHeadName}</td>
                  <td>{item?.departmentName}</td>
                  <td>{item?.members?.length}</td>
                  <td>{item?.initiatorName}</td>
                  <td>{FormatDateTime(item.createdDate, "DD/MM/YYYY")}</td>
                  <td>
                    {typeof item?.approvalStatus === "number"
                      ? getStatusText(item?.approvalStatus)
                      : item?.approvalStatus}
                  </td>
                  <td>
                    <TableActions
                      hasChildren={true}
                      url={matchedPermissionHr ? `${item.id}/view` : null}
                    >
                      {[
                        {
                          name: item?.approvalStatus !== 0 ? "Edit" : null,
                          action:
                            item?.approvalStatus !== 0
                              ? () => handleEdit(item.id)
                              : null,
                        },
                        {
                          name: matchedPermissionHr ? "View" : null,
                          action: () =>
                            matchedPermissionHr ? handleView(item.id) : null,
                        },
                        {
                          name: item?.approvalStatus !== 0 ? "Delete" : null,
                          action: () =>
                            item?.approvalStatus !== 0
                              ? handleOpenDeleteModal(item.id)
                              : null,
                        },
                      ]}
                    </TableActions>
                  </td>
                </tr>
              );
            })}
            {data?.result?.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center">
                  No Committee List Found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      <Pagination
        last_page={data?.responseObject?.totalNumberOfPages}
        present_page={data?.responseObject?.currentPage}
        totalRows={data?.responseObject?.totalCount}
        pageSize={pageSize}
        setPageSize={(page) => setPageSize(page)}
        click={(page) => setCurrentPage(page)}
      />
      {openModal && selectedId && (
        <AppModalTemplate
          width={"600px"}
          isOpen={openModal}
          setIsOpen={handleModal}
        >
          <EditCommitteeIndexModal
            isOpen={openModal}
            setIsOpen={handleModal}
            onValidate={handleEditCommitteeValidation}
            openNext={(value) => {
              setEditInitialModal({
                committeeHeadId: parseInt(value.committeeHeadId),
                departmentId: parseInt(value.departmentId),
                description: value.description,
                title: value.title,
              });
            }}
            id={selectedId}
          />
        </AppModalTemplate>
      )}
      {openMemberModal && (
        <AppModalTemplate
          width={"600px"}
          isOpen={openMemberModal}
          setIsOpen={handleMemberModal}
        >
          <MemberModal
            isOpen={openMemberModal}
            setIsOpen={handleMemberModal}
            onValidate={() => {
              setCurrentCreateStep(3);
              setOpenMemberModal(false);
              handleDetailModal();
            }}
            openNext={(value) => {
              // Cache member data
              setCachedCreateData((prev) => ({ ...prev, members: value }));
              setCommitteeFloss({
                selectedMembers: value.selectedMembers,
                memberIds: value.memberIds,
              });
            }}
            onBack={handleCreateBackFromMember}
            cachedData={cachedCreateData.members}
          />
        </AppModalTemplate>
      )}
      {detailModal && (
        <AppModalTemplate
          width={"600px"}
          isOpen={detailModal}
          setIsOpen={handleDetailModal}
        >
          <CommitteeDetailModal
            isOpen={detailModal}
            setIsOpen={handleDetailModal}
            onValidate={() => {
              setDetailModal(false);
            }}
            openNext={() => {
              setCurrentCreateStep(4);
              setDetailModal(false);
              handleKraModal();
            }}
            onBack={handleCreateBackFromDetail}
            departmentId={initialModal.departmentId}
            title={initialModal.title}
            description={initialModal.description}
            communityHead={initialModal.committeeHeadId}
            communityMembers={committeeFloss.selectedMembers}
          />
        </AppModalTemplate>
      )}
      {kraModal && (
        <AppModalTemplate
          width={"600px"}
          isOpen={kraModal}
          setIsOpen={handleKraModal}
        >
          <CommitteKRAModal
            isOpen={kraModal}
            setIsOpen={handleKraModal}
            onValidate={() => {
              setKraModal(false);
              setCurrentCreateStep(1); // Reset after completion
            }}
            onBack={handleCreateBackFromKRA}
            title={initialModal.title}
            description={initialModal.description}
            committeeHeadId={initialModal.committeeHeadId}
            departmentId={initialModal.departmentId}
            memberIds={committeeFloss.memberIds}
          />
        </AppModalTemplate>
      )}
      {openCreatModal && (
        <AppModalTemplate
          width={"600px"}
          isOpen={openCreatModal}
          setIsOpen={handleCloseCreateModal}
        >
          <CreateCommitteeIndexModal
            isOpen={openCreatModal}
            setIsOpen={handleCloseCreateModal}
            onValidate={handleCommitteeValidation}
            openNext={(value) => {
              setInitialModal({
                committeeHeadId: parseInt(value.committeeHeadId),
                departmentId: parseInt(value.departmentId),
                description: value.description,
                title: value.title,
              });
            }}
            cachedData={cachedCreateData.committee}
          />
        </AppModalTemplate>
      )}

      {/* Edit flow modals */}
      {openEditMemberModal && (
        <AppModalTemplate
          width={"600px"}
          isOpen={openEditMemberModal}
          setIsOpen={handleEditMemberModal}
        >
          <EditMemberModal
            isOpen={openEditMemberModal}
            setIsOpen={handleEditMemberModal}
            onValidate={() => {
              setCurrentEditStep(3);
              setOpenEditMemberModal(false);
              handleEditDetailModal();
            }}
            openNext={(value) => {
              // Cache member data
              setCachedEditData((prev) => ({ ...prev, members: value }));
              setEditCommitteeFloss({
                selectedMembers: value.selectedMembers,
                memberIds: value.memberIds,
              });
            }}
            onBack={handleEditBackFromMember}
            cachedData={cachedEditData.members}
            id={selectedId}
            existingDepartmentId={editInitialModal.departmentId}
          />
        </AppModalTemplate>
      )}
      {openEditDetailModal && (
        <AppModalTemplate
          width={"600px"}
          isOpen={openEditDetailModal}
          setIsOpen={handleEditDetailModal}
        >
          <EditDetailModal
            isOpen={openEditDetailModal}
            setIsOpen={handleEditDetailModal}
            onValidate={() => {
              setOpenEditDetailModal(false);
            }}
            openNext={() => {
              setCurrentEditStep(4);
              setOpenEditDetailModal(false);
              handleEditKraModal();
            }}
            onBack={handleEditBackFromDetail}
            title={editInitialModal.title}
            description={editInitialModal.description}
            communityHead={editInitialModal.committeeHeadId}
            communityMembers={editCommitteeFloss.selectedMembers}
            id={selectedId}
          />
        </AppModalTemplate>
      )}
      {openEditKraModal && (
        <AppModalTemplate
          width={"600px"}
          isOpen={openEditKraModal}
          setIsOpen={handleEditKraModal}
        >
          <EditCommitteeKRAModal
            isOpen={openEditKraModal}
            setIsOpen={handleEditKraModal}
            onValidate={() => {
              setOpenEditKraModal(false);
              setCurrentEditStep(1); // Reset after completion
              // Refresh the committee list after successful update
              window.location.reload();
            }}
            onBack={handleEditBackFromKRA}
            title={editInitialModal.title}
            description={editInitialModal.description}
            committeeHeadId={editInitialModal.committeeHeadId}
            departmentId={editInitialModal.departmentId}
            memberIds={editCommitteeFloss.memberIds}
            committeeId={selectedId}
            id={selectedId}
          />
        </AppModalTemplate>
      )}

      <DeleteItemModal
        isOpen={openDelete}
        handleClose={handleCloseDelete}
        handleDelete={handleDelete}
        headText={"Delete Committee List"}
        infoText={"Are you sure you want to Delete this Committee List?"}
      />
    </PageStyle>
  );
};

export default CommitteeList;
